/**
 * Naive UI Core Theme Configuration
 * Based on: https://www.naiveui.com/en-US/dark/components/global-style
 */

import type { GlobalThemeOverrides } from 'naive-ui'
import { darken, lighten, rgba } from 'polished'

/**
 * Design System Tokens
 */
export const designTokens = {
  // Color Palette
  colors: {
    primary: '#764ab9',
    secondary: '#4a90e2',
    success: '#18a058',
    warning: '#f0a020',
    error: '#d03050',
    info: '#2080f0',

    // Neutral Colors
    white: '#ffffff',
    black: '#000000',

    // Gray Scale
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
  },

  // Typography
  typography: {
    fontFamily: {
      base: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: '"Fira Code", "Courier New", monospace',
    },
    fontSize: {
      tiny: '12px',
      small: '14px',
      medium: '16px',
      large: '18px',
      huge: '20px',
    },
    lineHeight: {
      tight: 1.25,
      base: 1.5,
      relaxed: 1.75,
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // Spacing
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px',
  },

  // Border Radius
  borderRadius: {
    small: '4px',
    medium: '8px',
    large: '12px',
    xlarge: '16px',
    full: '9999px',
  },

  // Shadows
  boxShadow: {
    'sm': '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    'base': '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    'md': '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    'lg': '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    'xl': '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },

  // Transitions
  transition: {
    fast: '150ms',
    base: '200ms',
    slow: '300ms',
    slower: '500ms',
  },

  // Z-Index
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
}

/**
 * Generate Light Theme Overrides
 */
export function generateLightTheme(primaryColor?: string): GlobalThemeOverrides {
  const baseColor = primaryColor || designTokens.colors.primary

  return {
    common: {
      // Primary Colors
      primaryColor: baseColor,
      primaryColorHover: lighten(0.1, baseColor),
      primaryColorPressed: darken(0.1, baseColor),
      primaryColorSuppl: lighten(0.15, baseColor),

      // Info Colors
      infoColor: designTokens.colors.info,
      infoColorHover: lighten(0.1, designTokens.colors.info),
      infoColorPressed: darken(0.1, designTokens.colors.info),
      infoColorSuppl: lighten(0.15, designTokens.colors.info),

      // Success Colors
      successColor: designTokens.colors.success,
      successColorHover: lighten(0.1, designTokens.colors.success),
      successColorPressed: darken(0.1, designTokens.colors.success),
      successColorSuppl: lighten(0.15, designTokens.colors.success),

      // Warning Colors
      warningColor: designTokens.colors.warning,
      warningColorHover: lighten(0.1, designTokens.colors.warning),
      warningColorPressed: darken(0.1, designTokens.colors.warning),
      warningColorSuppl: lighten(0.15, designTokens.colors.warning),

      // Error Colors
      errorColor: designTokens.colors.error,
      errorColorHover: lighten(0.1, designTokens.colors.error),
      errorColorPressed: darken(0.1, designTokens.colors.error),
      errorColorSuppl: lighten(0.15, designTokens.colors.error),

      // Text Colors
      textColorBase: designTokens.colors.black,
      textColor1: 'rgba(0, 0, 0, 0.9)',
      textColor2: 'rgba(0, 0, 0, 0.82)',
      textColor3: 'rgba(0, 0, 0, 0.52)',

      textColorDisabled: 'rgba(0, 0, 0, 0.82)',

      // Placeholder Color
      placeholderColor: 'rgba(0, 0, 0, 0.38)',

      // Base Colors
      baseColor: designTokens.colors.white,
      cardColor: designTokens.colors.white,
      modalColor: designTokens.colors.white,
      popoverColor: '#ffffff',
      bodyColor: '#ffffff',
      tagColor: designTokens.colors.gray[100],

      // Border Colors
      borderColor: designTokens.colors.gray[200],
      dividerColor: designTokens.colors.gray[200],

      // Table Colors
      tableHeaderColor: designTokens.colors.gray[50],
      tableColor: designTokens.colors.white,
      tableColorHover: designTokens.colors.gray[50],
      tableColorStriped: designTokens.colors.gray[50],

      // Hover Colors
      hoverColor: rgba(designTokens.colors.gray[500], 0.08),
      // '--app-hover-bg': rgba(designTokens.colors.primary, 0.08),

      // Typography
      fontFamily: designTokens.typography.fontFamily.base,
      fontFamilyMono: designTokens.typography.fontFamily.mono,
      fontSize: designTokens.typography.fontSize.small,
      fontSizeTiny: designTokens.typography.fontSize.tiny,
      fontSizeSmall: designTokens.typography.fontSize.small,
      fontSizeMedium: designTokens.typography.fontSize.small,
      fontSizeLarge: designTokens.typography.fontSize.small,
      fontSizeHuge: designTokens.typography.fontSize.huge,
      fontWeightStrong: String(designTokens.typography.fontWeight.semibold),
      lineHeight: String(designTokens.typography.lineHeight.base),

      // Border Radius
      borderRadius: designTokens.borderRadius.large,
      borderRadiusSmall: designTokens.borderRadius.small,

      // Box Shadows
      boxShadow1: designTokens.boxShadow.sm,
      boxShadow2: designTokens.boxShadow.base,
      boxShadow3: designTokens.boxShadow.md,

      // Heights
      heightTiny: '24px',
      heightSmall: '32px',
      heightMedium: '40px',
      heightLarge: '48px',
      heightHuge: '56px',

      // Close Icon Colors
      closeIconColor: 'rgba(0, 0, 0, 0.45)',
      closeIconColorHover: 'rgba(0, 0, 0, 0.58)',
      closeIconColorPressed: 'rgba(0, 0, 0, 0.52)',
      closeColorHover: 'rgba(0, 0, 0, 0.09)',
      closeColorPressed: 'rgba(0, 0, 0, 0.13)',

      // Clear Icon Colors
      clearColor: 'rgba(0, 0, 0, 0.38)',
      clearColorHover: 'rgba(0, 0, 0, 0.48)',
      clearColorPressed: 'rgba(0, 0, 0, 0.42)',

      // Scrollbar
      scrollbarColor: 'rgba(0, 0, 0, 0.25)',
      scrollbarColorHover: 'rgba(0, 0, 0, 0.4)',
      scrollbarWidth: '6px',
      scrollbarHeight: '6px',
      scrollbarBorderRadius: '3px',

      // Progress
      railColor: designTokens.colors.gray[200],

      // Action Colors
      actionColor: 'rgba(0, 0, 0, 0.06)',

      // Opacity
      opacityDisabled: '0.38',

      // Cubic Bezier
      cubicBezierEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      cubicBezierEaseOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      cubicBezierEaseIn: 'cubic-bezier(0.4, 0, 1, 1)',
    },
		 Tooltip: {
      color: '#ffffff',
      textColor: 'rgba(0, 0, 0, 0.82)',
    },
  }
}

/**
 * Generate Dark Theme Overrides
 */
export function generateDarkTheme(primaryColor?: string): GlobalThemeOverrides {
  const baseColor = primaryColor || designTokens.colors.primary

  return {
    common: {
      // Primary Colors
      primaryColor: baseColor,
      primaryColorHover: lighten(0.15, baseColor),
      primaryColorPressed: darken(0.05, baseColor),
      primaryColorSuppl: lighten(0.2, baseColor),

      // Info Colors
      infoColor: lighten(0.1, designTokens.colors.info),
      infoColorHover: lighten(0.2, designTokens.colors.info),
      infoColorPressed: designTokens.colors.info,
      infoColorSuppl: lighten(0.25, designTokens.colors.info),

      // Success Colors
      successColor: lighten(0.1, designTokens.colors.success),
      successColorHover: lighten(0.2, designTokens.colors.success),
      successColorPressed: designTokens.colors.success,
      successColorSuppl: lighten(0.25, designTokens.colors.success),

      // Warning Colors
      warningColor: lighten(0.1, designTokens.colors.warning),
      warningColorHover: lighten(0.2, designTokens.colors.warning),
      warningColorPressed: designTokens.colors.warning,
      warningColorSuppl: lighten(0.25, designTokens.colors.warning),

      // Error Colors
      errorColor: lighten(0.1, designTokens.colors.error),
      errorColorHover: lighten(0.2, designTokens.colors.error),
      errorColorPressed: designTokens.colors.error,
      errorColorSuppl: lighten(0.25, designTokens.colors.error),

      // Text Colors
      textColorBase: designTokens.colors.white,
      textColor1: 'rgba(255, 255, 255, 0.9)',
      textColor2: 'rgba(255, 255, 255, 0.82)',
      textColor3: 'rgba(255, 255, 255, 0.52)',

      textColorDisabled: 'rgba(255, 255, 255, 0.82)',

      // Placeholder Color
      placeholderColor: 'rgba(255, 255, 255, 0.38)',

      // Base Colors
      baseColor: designTokens.colors.white,
      cardColor: '#18181c',
      modalColor: '#18181c',
      popoverColor: '#18181c',
      bodyColor: '#101014',
      tagColor: '#27272a',

      // Border Colors
      borderColor: '#3f3f46',
      dividerColor: '#3f3f46',

      // Table Colors
      tableHeaderColor: '#27272a',
      tableColor: '#18181c',
      tableColorHover: '#27272a',
      tableColorStriped: '#27272a',

      // Hover Colors
      hoverColor: 'rgba(255, 255, 255, 0.06)',
      // '--app-hover-bg': rgba('#ffffff', 0.08),

      // Typography
      fontFamily: designTokens.typography.fontFamily.base,
      fontFamilyMono: designTokens.typography.fontFamily.mono,
      fontSize: designTokens.typography.fontSize.small,
      fontSizeTiny: designTokens.typography.fontSize.tiny,
      fontSizeSmall: designTokens.typography.fontSize.small,
      fontSizeMedium: designTokens.typography.fontSize.small,
      fontSizeLarge: designTokens.typography.fontSize.small,
      fontSizeHuge: designTokens.typography.fontSize.huge,
      fontWeightStrong: String(designTokens.typography.fontWeight.semibold),
      lineHeight: String(designTokens.typography.lineHeight.base),

      // Border Radius
      borderRadius: designTokens.borderRadius.medium,
      borderRadiusSmall: designTokens.borderRadius.small,

      // Box Shadows
      boxShadow1: '0 1px 2px 0 rgba(0, 0, 0, 0.24)',
      boxShadow2: '0 3px 6px 0 rgba(0, 0, 0, 0.18), 0 1px 3px 0 rgba(0, 0, 0, 0.16)',
      boxShadow3: '0 6px 16px -9px rgba(0, 0, 0, 0.08), 0 9px 28px 0 rgba(0, 0, 0, 0.05), 0 12px 48px 16px rgba(0, 0, 0, 0.03)',

      // Heights
      heightTiny: '24px',
      heightSmall: '32px',
      heightMedium: '40px',
      heightLarge: '48px',
      heightHuge: '56px',

      // Close Icon Colors
      closeIconColor: 'rgba(255, 255, 255, 0.52)',
      closeIconColorHover: 'rgba(255, 255, 255, 0.68)',
      closeIconColorPressed: 'rgba(255, 255, 255, 0.62)',
      closeColorHover: 'rgba(255, 255, 255, 0.09)',
      closeColorPressed: 'rgba(255, 255, 255, 0.13)',

      // Clear Icon Colors
      clearColor: 'rgba(255, 255, 255, 0.38)',
      clearColorHover: 'rgba(255, 255, 255, 0.48)',
      clearColorPressed: 'rgba(255, 255, 255, 0.42)',

      // Scrollbar
      scrollbarColor: 'rgba(255, 255, 255, 0.25)',
      scrollbarColorHover: 'rgba(255, 255, 255, 0.4)',
      scrollbarWidth: '6px',
      scrollbarHeight: '6px',
      scrollbarBorderRadius: '3px',

      // Progress
      railColor: '#3f3f46',

      // Action Colors
      actionColor: 'rgba(255, 255, 255, 0.09)',

      // Opacity
      opacityDisabled: '0.38',

      // Cubic Bezier
      cubicBezierEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      cubicBezierEaseOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      cubicBezierEaseIn: 'cubic-bezier(0.4, 0, 1, 1)',
    },
    Tooltip: {
      color: '#18181c',
      textColor: 'rgba(255, 255, 255, 0.82)',
    },
  }
}

/**
 * Component-specific Theme Overrides
 */
export const componentThemeOverrides: GlobalThemeOverrides = {
  Button: {
    borderRadiusMedium: designTokens.borderRadius.full,
    borderRadiusSmall: designTokens.borderRadius.full,
    borderRadiusLarge: designTokens.borderRadius.full,
    heightMedium: '40px',
    heightLarge: '48px',
    heightSmall: '32px',
    heightTiny: '24px',
    fontSizeMedium: designTokens.typography.fontSize.small,
    fontSizeLarge: designTokens.typography.fontSize.small,
    fontSizeSmall: designTokens.typography.fontSize.small,
    paddingMedium: '0 16px',
    paddingLarge: '0 20px',
    paddingSmall: '0 12px',
    iconMarginMedium: '6px',
    iconMarginLarge: '8px',
    iconMarginSmall: '4px',
    fontWeight: '500',
    fontWeightStrong: '500',
  },

  Input: {
    borderRadius: designTokens.borderRadius.large,
    heightMedium: '40px',
    heightLarge: '48px',
    heightSmall: '32px',
    heightTiny: '24px',
    fontSizeMedium: designTokens.typography.fontSize.small,
    fontSizeLarge: designTokens.typography.fontSize.small,
    fontSizeSmall: designTokens.typography.fontSize.small,
    paddingMedium: '0 12px',
    paddingLarge: '0 14px',
    paddingSmall: '0 10px',
  },

  InputOtp: {
    inputWidthSmall: '28px',
    inputWidthMedium: '34px',
    inputWidthLarge: '40px',
  },

  Select: {
    menuBoxShadow: designTokens.boxShadow.lg,
  },

  Dropdown: {
    borderRadius: designTokens.borderRadius.large,
    boxShadow: designTokens.boxShadow.lg,
    optionColorHover: 'var(--n-hover-color)',
    optionTextColorHover: 'var(--n-text-color)',
  },

  Card: {
    borderRadius: designTokens.borderRadius.large,
    paddingMedium: '20px',
    paddingLarge: '24px',
    paddingSmall: '16px',
  },

  Modal: {
    borderRadius: designTokens.borderRadius.xlarge,
    boxShadow: designTokens.boxShadow['2xl'],
  },

  Dialog: {
    borderRadius: designTokens.borderRadius.xlarge,
    padding: '24px',
    iconMargin: '12px',
  },

  Notification: {
    borderRadius: designTokens.borderRadius.large,
    padding: '16px',
  },

  Message: {
    borderRadius: designTokens.borderRadius.large,
    padding: '12px 16px',
  },

  Table: {
    borderRadius: designTokens.borderRadius.large,
    thPaddingSmall: '6px',
    thPaddingMedium: '16px',
    thPaddingLarge: '16px',
    tdPaddingSmall: '6px',
    tdPaddingMedium: '16px',
    tdPaddingLarge: '16px',
  },

  Tabs: {
    tabBorderRadius: designTokens.borderRadius.medium,
    tabGapMediumCard: '4px',
    tabGapLargeCard: '6px',
    tabGapSmallCard: '2px',
    tabPaddingMediumCard: '8px 16px',
    tabPaddingLargeCard: '10px 20px',
    tabPaddingSmallCard: '6px 12px',
  },

  Tag: {
    borderRadius: designTokens.borderRadius.small,
    padding: '0 8px',
  },

  Pagination: {
    itemBorderRadius: designTokens.borderRadius.medium,
  },

  Popover: {
    borderRadius: designTokens.borderRadius.large,
    boxShadow: designTokens.boxShadow.lg,
  },

  Switch: {
    railHeightMedium: '20px',
    railWidthMedium: '40px',
    railHeightSmall: '16px',
    railWidthSmall: '32px',
    railHeightLarge: '24px',
    railWidthLarge: '48px',
    buttonWidthMedium: '16px',
    buttonHeightMedium: '16px',
    buttonWidthSmall: '12px',
    buttonHeightSmall: '12px',
    buttonWidthLarge: '20px',
    buttonHeightLarge: '20px',
  },

  Checkbox: {
    borderRadius: designTokens.borderRadius.small,
    sizeMedium: '18px',
    sizeSmall: '16px',
    sizeLarge: '20px',
  },

  Radio: {
    buttonBorderRadius: designTokens.borderRadius.medium,
    sizeMedium: '18px',
    sizeSmall: '14px',
    sizeLarge: '22px',
    radioSizeSmall: '18px',
    radioSizeMedium: '20px',
    radioSizeLarge: '22px',
  },

  Slider: {
    railHeight: '6px',
    handleSize: '18px',
    dotHeight: '8px',
    dotWidth: '8px',
  },

  Progress: {
    railHeight: '8px',
    borderRadius: designTokens.borderRadius.full,
  },

  Badge: {
    height: '18px',
    fontSize: '12px',
  },

  Avatar: {
    borderRadius: designTokens.borderRadius.full,
  },

  DatePicker: {
    panelBorderRadius: designTokens.borderRadius.large,
  },

  TimePicker: {
    panelBorderRadius: designTokens.borderRadius.large,
  },

  ColorPicker: {
    borderRadius: designTokens.borderRadius.large,
  },

  Upload: {
    borderRadius: designTokens.borderRadius.large,
  },

  Form: {
    labelFontSizeTopMedium: designTokens.typography.fontSize.small,
    labelHeightMedium: '24px',
    labelPaddingVertical: '0 0 8px 2px',
    feedbackHeightMedium: '24px',
    feedbackFontSizeMedium: designTokens.typography.fontSize.small,
  },

  Menu: {
    itemColorHover: 'var(--n-hover-color)',
    itemTextColorHover: 'var(--n-text-color)',
  },
}

/**
 * Default Theme Export
 */
export const defaultLightTheme = {
  ...generateLightTheme(),
  ...componentThemeOverrides,
}

export const defaultDarkTheme = {
  ...generateDarkTheme(),
  ...componentThemeOverrides,
}
